import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import SectionLabel from "./SectionLabel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CTAFinal() {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "", position: null });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setState({ status: "error", message: t.cta.invalid, position: null });
      return;
    }
    setLoading(true);
    setState({ status: "idle", message: "", position: null });
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: trimmed,
        locale,
        source: "landing",
      });
      const { position, already_registered } = res.data;
      setState({
        status: "success",
        message: already_registered ? t.cta.already : t.cta.success,
        position,
      });
      if (!already_registered) setEmail("");
    } catch (err) {
      setState({ status: "error", message: t.cta.error, position: null });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cta" data-testid="cta-section" className="relative py-20 md:py-32 border-t border-line overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 60% at 50% 30%, rgba(0,255,136,0.14), transparent 70%)",
        }}
      />
      <div className="container-px mx-auto max-w-4xl relative">
        <div className="text-center">
          <SectionLabel className="justify-center">{t.cta.label}</SectionLabel>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-6 font-display font-bold text-ink text-balance leading-[1.02]
                       text-4xl sm:text-6xl lg:text-7xl"
          >
            {t.cta.title}
          </motion.h2>
          <p className="mt-5 text-ink/65 max-w-xl mx-auto text-base md:text-lg">
            {t.cta.subtitle}
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.15 }}
          onSubmit={submit}
          className="mt-10 max-w-xl mx-auto"
          data-testid="waitlist-form"
        >
          <div className="flex flex-col sm:flex-row gap-2 p-2 hairline rounded-2xl"
               style={{ background: "rgba(28,31,38,0.6)", backdropFilter: "blur(8px)" }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.cta.placeholder}
              className="input-tactical flex-1 !border-transparent !bg-transparent focus:!shadow-none"
              required
              autoComplete="email"
              data-testid="waitlist-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-neon !rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="waitlist-submit"
            >
              {loading ? t.cta.loading : t.cta.button}
              {!loading && <ArrowRight size={14} />}
            </button>
          </div>

          {state.status === "success" && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 flex items-center justify-center gap-2 mono-num text-sm neon-text"
              data-testid="waitlist-success"
            >
              <Check size={14} />
              <span>{state.message}{state.position}</span>
            </motion.div>
          )}
          {state.status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 flex items-center justify-center gap-2 mono-num text-sm text-danger"
              data-testid="waitlist-error"
            >
              <AlertTriangle size={14} />
              <span>{state.message}</span>
            </motion.div>
          )}

          <p className="mt-4 label-tag text-center !text-[10px] text-ink/40">
            {t.cta.privacy}
          </p>
        </motion.form>
      </div>
    </section>
  );
}
